import { action, makeObservable, observable } from "mobx";
import axios from "axios";
import { ADMIN_STATION_URL, NOW_PLAYING } from "shared/config/backendPaths";

import { IStationLive, INowPlayingResponseItem } from "../types/IStationLive";

const POLL_INTERVAL = 15000;

/**
 * Живое состояние всех станций (online + число слушателей) для кубиков и
 * сводного виджета. Один публичный запрос GET /api/nowplaying покрывает все
 * станции сразу; опрашиваем раз в 15 сек. Синглтон, ссылочный счётчик —
 * опрос идёт, пока есть хотя бы один подписчик.
 */
export class StationsLiveStore {
    private static shared: StationsLiveStore | null = null;

    private timer: ReturnType<typeof setInterval> | null = null;

    private subscribers = 0;

    /** ключ — shortcode станции (deep/dnb/house) */
    public live: Map<string, IStationLive> = new Map();

    constructor() {
        makeObservable(this, {
            live: observable,
            setLive: action,
        });
    }

    async fetch() {
        try {
            const res = await axios.get<INowPlayingResponseItem[]>(ADMIN_STATION_URL + NOW_PLAYING);
            if (!res || !Array.isArray(res.data)) return;

            const next = new Map<string, IStationLive>();
            for (const item of res.data) {
                const code = item?.station?.shortcode;
                if (!code) continue;
                next.set(code, {
                    shortcode: code,
                    name: item?.station?.name ?? code,
                    online: Boolean(item.is_online),
                    listeners: item?.listeners?.current ?? 0,
                });
            }
            this.setLive(next);
        } catch {
            /* сеть/азура недоступна — оставляем прошлые данные, кубики не ломаем */
        }
    }

    /** Начать опрос (идемпотентно). Возвращает функцию отписки. */
    start(): () => void {
        this.subscribers += 1;
        if (this.timer === null) {
            this.fetch();
            this.timer = setInterval(() => this.fetch(), POLL_INTERVAL);
        }
        return () => this.stop();
    }

    private stop() {
        this.subscribers = Math.max(0, this.subscribers - 1);
        if (this.subscribers === 0 && this.timer !== null) {
            clearInterval(this.timer);
            this.timer = null;
        }
    }

    get(shortcode: string | null | undefined): IStationLive | null {
        if (!shortcode) return null;
        return this.live.get(shortcode) ?? null;
    }

    setLive(value: Map<string, IStationLive>) {
        this.live = value;
    }

    static getShared(): StationsLiveStore {
        if (!this.shared) {
            this.shared = new StationsLiveStore();
        }
        return this.shared;
    }
}
